import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Logger } from 'projects/common/src/app/classes';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { ParameterHandlerService } from 'projects/common/src/app/services';
import { Subscription } from 'rxjs';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { ESidebarState } from 'src/app/modules/sidebar/enumerables/sidebarState';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { IWidgetConfiguration } from 'src/app/modules/widget/interfaces/widget';
import { IWidgetSendConfiguration } from 'src/app/modules/widget/interfaces/widget-send-configuration';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { DashboardService } from '../../services/dashboard/dashboard.service';
import { DashboardParameterService } from '../../services/parameters/dashboard-parameter.service';
import { getMoment } from 'src/app/modules/core/services/datetime.service';
import { Moment } from 'moment';
import { IShift } from 'src/app/modules/core/interfaces';
import { LoadingStateService } from 'src/app/modules/widget/services/loading-state.service';

@Component({
  selector: 'app-default-dashboard-layout',
  templateUrl: './default-dashboard-layout.component.html',
  styleUrls: ['./default-dashboard-layout.component.scss'],
})
export class DefaultDashboardLayoutComponent extends Logger implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('dashboard', { static: false })
  public dashboardView: ElementRef<any>;
  private widgetVisibilityState: { [key: number]: boolean } = {}; // Visibility state of widgets
  private scrollTimeout: any; // Timeout for scroll event
  private isInitialLoad = true; // Flag to indicate initial load
  public refreshInterval: any; // Interval for refreshing widgets
  public previousShift: IShift; // Previous shift value
  public widgetsInitialized = false; // Flag to indicate if the dashboard is initialized
  public name = ''; // Initialize name as empty string

  public viewWidgetCollection: IWidgetConfiguration[];
  public currentMachine: IMachineContext;
  public lastVisualScreen: number;
  public machineContextSub: Subscription;
  public currentDay: string;
  public secondTitle: string;
  protected activeRouteSubscription: Subscription;

  constructor(
    protected sidebarService: StateSidebarService,
    private dashboardParameter: DashboardParameterService,
    private parameterService: ParameterHandlerService,
    private screenViewService: ScreenViewService,
    private dashboardService: DashboardService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _loadingStateService: LoadingStateService,
  ) {
    super();
    this.viewWidgetCollection = [];
    this.parameterService.subscribe(dashboardParameter);
    this.currentMachine = null;
  }

  public ngOnInit(): void {
    this._loadingStateService.start();
    this.initializeDashboard();
  }

  public ngAfterViewInit(): void {
    // machineContext subscription
    this.machineContextSub = this.dashboardService.machineContext$.subscribe(async (machineContext) => {
      // Verifies if the machine context is complete, or attempts to complete it
      if (!machineContext?.code || !machineContext?.machineTypeId) {
        console.warn('Machine context incomplete. Attempting to complete it...');
        const completedContext = await this.completeMachineContext(machineContext);
        console.warn('Completed machine context:', completedContext);
        this.currentMachine = completedContext;
      } else {
        this.currentMachine = machineContext;
      }

      const dashboardConfig = this.dashboardParameter.get();
      // Verifies if currentMachine and viewWidgetCollection are ready, then initializes the widgets
      if (this.currentMachine && this.viewWidgetCollection.length) {
        if (!this.widgetsInitialized) {
          this.widgetsInitialized = false;
          setTimeout(() => {
            this.cancelAllWidgetRequests(); // 👨‍🚀 evita conflictos con dsh anterior
            this.setWidgetConfiguration(dashboardConfig.widgets);
            this.widgetsInitialized = true;
          });
        } else {
          this.handleParameterChange();
        }
      } else {
        console.warn('Machine context or widgets not ready.');
      }
      this._loadingStateService.start();
      this.scrollToTop();
    });

    this.parameterService.parameterSource.item('shift').subscribe((newShift) => {
      if (!this.isInitialLoad && newShift !== this.previousShift) {
        this.previousShift = newShift;
        this.handleParameterChange();
      } else {
        console.log('⏭ Ignoring unnecessary shift changes.');
      }
    });

    this.parameterService.parameterSource.item('date').subscribe(() => {
      if (!this.isInitialLoad) {
        console.log('🆕 When changing the date, it will be grouped with bounceTime.');
        this.scrollToTop();
        this.applyDateBasedRestrictions();
        this.handleParameterChange();
      }
    });

    // Mark initial load as completed
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 0);
  }

  public ngOnDestroy(): void {
    this.machineContextSub.unsubscribe();
    // Stop refresh interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      console.log('🛑 Update interval stopped');
    }
    this._loadingStateService.complete();
  }

  private initializeWidgets(): void {
    if (this.currentMachine && this.currentMachine.code && this.viewWidgetCollection.length) {
      this.initVisibleWidgetsOnly();
    } else {
      console.warn('Widgets cannot be initialized yet.');
    }
  }

  private async completeMachineContext(machineContext: IMachineContext): Promise<IMachineContext> {
    const machineCollection = JSON.parse(sessionStorage.getItem('machineCollection')) as IMachineContext[];

    if (Array.isArray(machineCollection)) {
      const matchingMachine = machineCollection.find((machine) => machine.name.toLowerCase() === machineContext?.name?.toLowerCase());

      if (matchingMachine) {
        return {
          ...machineContext,
          code: matchingMachine.code || machineContext.code,
          hasProduction: matchingMachine.hasProduction || machineContext.hasProduction,
          machineTypeId: matchingMachine.machineTypeId || machineContext.machineTypeId,
        };
      }
    }

    return machineContext; // If no matching machine found, return the original context
  }

  /**
   * Handle parameter change (date, shift, etc.)
   * Cancels requests for widgets that are not visible on screen
   */
  private handleParameterChange(): void {
    this._loadingStateService.start();
    const isToday = this.isTodayDate();

    if (!isToday) {
      console.log('📅 La fecha del dashboard NO es hoy. Se realizará un solo request para todos los widgets.');

      this.disableAutoUpdates();

      // Send a single request for all widgets
      this.viewWidgetCollection.forEach((widgetConfig) => {
        this.screenViewService.widgetCancelRequest.next({
          id: widgetConfig.rawComponent.componentId,
          onScreen: true,
        });
      });
    } else {
      console.log('📅 The dashboard date is NOT today. A single request will be made for all widgets.');
      this.enableAutoUpdates();

      // 🔥 FORCE UPDATE FOR ALL WIDGETS 🔥
      console.log('🔄 Refresh widgets inmediatly after change shift.');
      this.checkRequestState();

      this.viewWidgetCollection.forEach((widgetConfig) => {
        this.screenViewService.widgetCancelRequest.next({
          id: widgetConfig.rawComponent.componentId,
          onScreen: true,
        });
      });
    }
  }

  /**
   * Function to initialize dashboard params
   */
  private async initializeDashboard(): Promise<void> {
    try {
      const dashboardConfig = await this.dashboardService.getDashboardConfiguration();
      if (dashboardConfig && dashboardConfig.widgets?.length) {
        this.dashboardService.initializeDashboard();
        this.name = dashboardConfig.name || ''; // Use empty string as fallback
        this.dashboardParameter.set(dashboardConfig);
        this.setWidgetConfiguration(dashboardConfig.widgets);

        // Init widgets even if machine context is not ready
        if (!this.currentMachine) {
          console.warn('Machine context not ready. Widgets may not be fully functional.');
        }
        this.initializeWidgets();
      } else {
        console.warn('Dashboard configuration is empty or invalid.');
      }
    } catch (error) {
      console.error('Error initializing dashboard:', error);
    }
  }

  public onWidgetChange(config: IWidgetSendConfiguration): void {
    const foundWidget = this.viewWidgetCollection.find((v) => v.rawComponent.componentId === config.componentId);
    foundWidget.componentData = {
      ...foundWidget.componentData,
      class: config.class,
      delay: config.delay,
      timeout: config.timeout,
      title: config.visibleName,
    };

    foundWidget.order = config.orderElement;
    foundWidget.rawComponent = {
      ...foundWidget.rawComponent,
      class: config.class,
      delay: config.delay,
      description: config.description,
      isEnabled: config.isEnabled,
      visibleName: config.visibleName,
      timeout: config.timeout,
      orderElement: config.orderElement,
    };
  }

  /**
   * Setting the configuration of each widget
   * @params components - array of widget configurations
   * This function will set the widget configuration and initialize the widgets if the machine context is ready.
   */
  public setWidgetConfiguration(components: IWidgetConfiguration[]): void {
    if (!components?.length) {
      console.warn('No widgets to configure.');
      return;
    }

    // force widget initialization
    this.widgetsInitialized = false;
    this._loadingStateService.start();

    this.viewWidgetCollection = [];

    for (const component of components) {
      component.componentData.componentId = component.rawComponent.componentId;
    }

    setTimeout(() => {
      this.viewWidgetCollection = components.sort((a, b) => a.order - b.order);
      this.widgetsInitialized = true;
      // Try to initialize widgets if machine context is ready
      this.initializeWidgets();
    }, 0);
  }

  /**
   * Cancels all active widget requests
   * This method is called when the component is destroyed or when a new dashboard is loaded.
   */
  public cancelAllWidgetRequests(): void {
    console.log('Cancelling all active widget requests.');
    this.viewWidgetCollection.forEach((widget) => {
      this.screenViewService.widgetCancelRequest.next({
        id: widget.rawComponent.componentId,
        onScreen: false, // 👷🏽‍♂️ Explicitamente fuera de pantalla
      });
    });
  }

  public swipe(): void {
    this.sidebarService.setState(ESidebarState.open);
  }

  private scrollToTop(): void {
    if (this.dashboardView && this.dashboardView.nativeElement) {
      this.dashboardView.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } else {
      console.error('Dsh view is not available.');
    }
  }

  private initVisibleWidgetsOnly(): void {
    if (!this.dashboardView || !this.viewWidgetCollection) {
      console.error('Dsh view or widget collection is not available.');
      return;
    }

    const visualScreen = this.dashboardView.nativeElement.clientHeight;
    const row = this.dashboardView.nativeElement.querySelector('.row');
    if (!row) {
      console.error('Row element not found.');
      return;
    }

    const visibleWidgets = [];
    for (const widget of row.children) {
      if (widget.offsetTop < visualScreen && widget.offsetTop + widget.offsetHeight > 0) {
        const foundWidget = this.viewWidgetCollection.find((v) => v.rawComponent.componentId === Number(widget.id));
        if (foundWidget) {
          visibleWidgets.push(foundWidget.rawComponent.visibleName);

          // emit event for visible widgets only
          this.screenViewService.widgetCancelRequest.next({
            id: foundWidget.rawComponent.componentId,
            onScreen: true,
          });
        }
      }
    }
  }

  /**
   * Verify if widgets are within the visible screen and send a request to cancel or keep the current request.
   */
  public checkRequestState(): void {
    if (!this.dashboardView || !this.viewWidgetCollection) {
      console.error('Dashboard view or widget collection not available.');
      return;
    }

    const { scrollTop } = this.dashboardView.nativeElement;
    const visualScreen = scrollTop + this.dashboardView.nativeElement.clientHeight;

    const row = this.dashboardView.nativeElement.querySelector('.row');
    if (!row) {
      console.error('Row element not found.');
      return;
    }

    for (const widget of row.children) {
      if (widget.id) {
        const widgetId = Number(widget.id);
        const widgetTop = widget.offsetTop;
        const widgetBottom = widgetTop + widget.offsetHeight;

        const isVisible =
          (widgetTop >= scrollTop && widgetTop < visualScreen) ||
          (widgetBottom > scrollTop && widgetBottom <= visualScreen) ||
          (widgetTop < scrollTop && widgetBottom > visualScreen);

        // emit event for visible widgets only if visibility state changes
        if (this.widgetVisibilityState[widgetId] !== isVisible) {
          this.widgetVisibilityState[widgetId] = isVisible;

          const foundWidget = this.viewWidgetCollection.find((v) => v.rawComponent.componentId === widgetId);
          if (foundWidget) {
            // emit event for visible widgets only
            if (isVisible) {
              this.screenViewService.widgetCancelRequest.next({
                id: foundWidget.rawComponent.componentId,
                onScreen: true,
              });
            }
          }
        }
      }
    }
  }

  /**
   * Verifies if current date and sessionStorage date are the same
   * @returns boolean - true if current date is today
   */
  private isTodayDate(): boolean {
    const momentDate: Moment = getMoment(new Date());
    const isTodayDate = momentDate.format('YYYY-MM-DD');
    const dashboardDate = this.parameterService.parameters.item('date').value;
    return dashboardDate === isTodayDate;
  }

  /**
   * Apply date-based restrictions to widgets scroll and auto-update
   */
  public applyDateBasedRestrictions(): void {
    if (!this.isTodayDate()) {
      this.disableAutoUpdates();
    } else {
      this.enableAutoUpdates();
    }
  }

  private disableAutoUpdates(): void {
    console.log('🚫 Disabling automatic widget updates.');

    // Stop the refresh interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      console.log('🛑 Update interval stopped.');
    }

    // Remove scroll event listener
    if (this.dashboardView) {
      this.dashboardView.nativeElement.removeEventListener('scroll', this.onScroll);
      console.log('🛑 Scroll event disabled.');
    }
  }

  private enableAutoUpdates(): void {
    console.log('✅ Enabling automatic widget update.');

    // Restore the refresh interval
    if (!this.refreshInterval) {
      this.refreshInterval = setInterval(() => {
        this.checkRequestState();
        Object.entries(this.widgetVisibilityState).forEach(([widgetId, isVisible]) => {
          if (isVisible) {
            this.screenViewService.widgetCancelRequest.next({
              id: Number(widgetId),
              onScreen: true,
            });
          }
        });
      }, 30000);
      console.log('✅ Update interval restored.');
    }

    // Restore scroll event listener
    if (this.dashboardView) {
      this.dashboardView.nativeElement.addEventListener('scroll', this.onScroll);
      console.log('✅ Scroll event retriggered.');
    }
  }

  /**
   * Function to handle scroll event
   * @returns void
   */
  private onScroll = (): void => {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.checkRequestState();
    }, 900); // Debounce time
  };

  public get isInitialized(): boolean {
    return !!(this.currentMachine && this.currentMachine.name);
  }
}
